import { useTranslations } from 'use-intl';

// The tax-account transaction types with a catalog label (`consultantShell.taxTransaction`).
const KNOWN_TYPES = [
  'vat',
  'employer_contributions',
  'withheld_tax',
  'preliminary_tax',
  'corporate_tax',
  'interest_charged',
  'interest_credited',
  'deposit',
  'payout',
  'penalty',
] as const;
type KnownType = (typeof KNOWN_TYPES)[number];

function isKnownType(type: string): type is KnownType {
  return (KNOWN_TYPES as readonly string[]).includes(type);
}

/**
 * What a tax-account transaction is called, in the language the app shows. An unrecognised type is
 * shown as the authority's own words, never forced into a nearby category: `other` is an expected
 * outcome, not a bug.
 */
export function useTaxTransactionLabel() {
  const t = useTranslations('consultantShell.taxTransaction');
  return (type: string, rawLabel: string | null): string => {
    if (isKnownType(type)) return t(type);
    return rawLabel ? t('otherWith', { label: rawLabel }) : t('other');
  };
}

/** The "Tax · <what it is>" caption a tax payable carries in lists and its details. */
export function useTaxPayableCaption() {
  const t = useTranslations('consultantShell.taxTransaction');
  const label = useTaxTransactionLabel();
  return (type: string, rawLabel: string | null): string => t('caption', { label: label(type, rawLabel) });
}
